import { useRef, useEffect, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  X,
  Loader2,
  AlertTriangle,
  Stethoscope,
  Building2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  departmentLabel,
  type Doctor,
  type Department,
  type ApprovalDecision,
} from '@refdash/api-client';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

type Step =
  | { type: 'choose' }
  | { type: 'accepted'; decision: 'accept' }
  | { type: 'rejected'; decision: 'reject' }
  | { type: 'enter-reject-reason' }
  | { type: 'reject-reason-entered'; reason: string }
  | { type: 'select-department' }
  | { type: 'department-selected'; department: Department }
  | { type: 'select-doctor' }
  | { type: 'doctor-selected'; doctor: Doctor }
  | { type: 'confirm-accept'; doctor: Doctor; department: Department }
  | { type: 'confirm-reject'; reason: string }
  | { type: 'submitting' }
  | { type: 'done'; decision: ApprovalDecision };

interface ChatDecisionProps {
  approvalId: number;
  doctors: Doctor[];
  departments: Department[];
  onDecided: () => void;
}

const bubbleIn = {
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
};

/** Doctors offered for a department: its own doctors first, then doctors with
 * no department (they serve any); doctors of other departments are excluded.
 * Mirrors the server's ?department_id= ordering. */
export function doctorsForDepartment(
  doctors: Doctor[],
  departmentId: number,
): Doctor[] {
  return doctors
    .filter((d) => d.department_id === departmentId || d.department_id == null)
    .sort((a, b) => {
      const rankA = a.department_id === departmentId ? 0 : 1;
      const rankB = b.department_id === departmentId ? 0 : 1;
      return rankA - rankB || a.name.localeCompare(b.name);
    });
}

/** Narrow the pickers to what this user's role may actually submit, mirroring
 * the server's delegation rules (delegation_service.check_doctor_choice and
 * the department-scope check). The server remains the authority; offering
 * choices it would reject only teaches the rule through a 403.
 *
 *   full authority (rep / coordinator) — everything
 *   department_head                    — own department; its doctors + floaters
 *   doctor                             — own department; only themselves
 */
export function scopeToAuthority(
  { role, departmentId, doctorId }: {
    role: string;
    departmentId: number | null;
    doctorId: number | null;
  },
  doctors: Doctor[],
  departments: Department[],
): { doctors: Doctor[]; departments: Department[] } {
  if (role !== 'department_head' && role !== 'doctor') {
    return { doctors, departments };
  }
  const scopedDepartments = departmentId == null
    ? []
    : departments.filter((d) => d.id === departmentId);

  if (role === 'doctor') {
    // A doctor may name only themselves. If their account isn't linked to a
    // doctor record the list is empty — the accept would 403 either way, and
    // an empty picker is a clearer signal than a rejected submission.
    return {
      doctors: doctors.filter((d) => d.id === doctorId),
      departments: scopedDepartments,
    };
  }
  return {
    doctors: departmentId == null
      ? []
      : doctorsForDepartment(doctors, departmentId),
    departments: scopedDepartments,
  };
}

export function ChatDecision({
  approvalId,
  doctors,
  departments,
  onDecided,
}: ChatDecisionProps) {
  const [steps, setSteps] = useState<Step[]>([{ type: 'choose' }]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const selectedDepartment =
    steps.find(
      (s): s is Extract<Step, { type: 'department-selected' }> =>
        s.type === 'department-selected',
    )?.department ?? null;
  const selectableDoctors = selectedDepartment
    ? doctorsForDepartment(doctors, selectedDepartment.id)
    : doctors;

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      // Scroll the page to the very bottom — the parent's pb-24 provides
      // clearance above the fixed bottom nav bar.
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    });
  }, []);

  useEffect(scrollToBottom, [steps, scrollToBottom]);

  function pushStep(step: Step) {
    setSteps((prev) => [...prev, step]);
  }

  function handleDecision(decision: ApprovalDecision) {
    if (decision === 'accept') {
      pushStep({ type: 'accepted', decision: 'accept' });
      setTimeout(() => pushStep({ type: 'select-department' }), 400);
    } else {
      pushStep({ type: 'rejected', decision: 'reject' });
      setTimeout(() => pushStep({ type: 'enter-reject-reason' }), 400);
    }
  }

  function handleDepartmentSelect(dept: Department) {
    pushStep({ type: 'department-selected', department: dept });
    setTimeout(() => {
      pushStep({ type: 'select-doctor' });
    }, 400);
  }

  function handleDoctorSelect(doc: Doctor) {
    const deptStep = steps.find(
      (s): s is Extract<Step, { type: 'department-selected' }> =>
        s.type === 'department-selected',
    );
    pushStep({ type: 'doctor-selected', doctor: doc });
    setTimeout(() => {
      pushStep({
        type: 'confirm-accept',
        doctor: doc,
        department: deptStep!.department,
      });
    }, 400);
  }

  function handleRejectReason(reason: string) {
    pushStep({ type: 'reject-reason-entered', reason });
    setTimeout(() => pushStep({ type: 'confirm-reject', reason }), 400);
  }

  function handleGoBack() {
    setSteps([{ type: 'choose' }]);
  }

  async function submitDecision(
    decision: ApprovalDecision,
    doctorId?: number,
    departmentId?: number,
    reason?: string,
  ) {
    pushStep({ type: 'submitting' });
    try {
      await api.approvals.decide(approvalId, {
        decision,
        doctor_id: doctorId ?? null,
        department_id: departmentId ?? null,
        reason: reason ?? null,
      });
      pushStep({ type: 'done', decision });
      toast.success(
        decision === 'accept' ? 'Approval accepted' : 'Approval rejected',
      );
      setTimeout(onDecided, 1500);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to submit decision';
      toast.error(msg);
      setSteps((prev) => prev.filter((s) => s.type !== 'submitting'));
    }
  }

  return (
    <div
      ref={scrollRef}
      className="flex flex-col gap-2.5 overflow-y-auto px-1 pb-4 scroll-smooth"
    >
      <AnimatePresence mode="popLayout">
        {steps.map((step, i) => (
          <ChatStepRenderer
            key={`${step.type}-${i}`}
            step={step}
            isActive={i === steps.length - 1}
            doctors={selectableDoctors}
            departments={departments}
            onDecision={handleDecision}
            onDepartmentSelect={handleDepartmentSelect}
            onDoctorSelect={handleDoctorSelect}
            onRejectReason={handleRejectReason}
            onConfirmAccept={(doc, dept) =>
              submitDecision('accept', doc.id, dept.id)
            }
            onConfirmReject={(reason) => submitDecision('reject', undefined, undefined, reason)}
            onGoBack={handleGoBack}
          />
        ))}
      </AnimatePresence>
    </div>
  );
}

// ── Individual step renderers ─────────────────────────────────────────

interface StepRendererProps {
  step: Step;
  isActive: boolean;
  doctors: Doctor[];
  departments: Department[];
  onDecision: (d: ApprovalDecision) => void;
  onDepartmentSelect: (d: Department) => void;
  onDoctorSelect: (d: Doctor) => void;
  onRejectReason: (reason: string) => void;
  onConfirmAccept: (doc: Doctor, dept: Department) => void;
  onConfirmReject: (reason: string) => void;
  onGoBack: () => void;
}

function ChatStepRenderer(props: StepRendererProps) {
  const { step, isActive } = props;

  switch (step.type) {
    case 'choose':
      return (
        <motion.div {...bubbleIn} className="space-y-2.5">
          <SystemBubble>What would you like to do?</SystemBubble>
          {isActive && (
            <div className="flex gap-2 ml-2">
              <Button
                size="sm"
                className="rounded-lg bg-emerald-600 hover:bg-emerald-700 shadow-sm shadow-emerald-200 gap-1.5 text-xs h-8"
                onClick={() => props.onDecision('accept')}
              >
                <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5 text-xs h-8"
                onClick={() => props.onDecision('reject')}
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                Reject
              </Button>
            </div>
          )}
        </motion.div>
      );

    case 'accepted':
      return (
        <motion.div {...bubbleIn} className="flex justify-end">
          <UserBubble className="bg-emerald-600">
            <Check className="w-3 h-3" /> Accept
          </UserBubble>
        </motion.div>
      );

    case 'rejected':
      return (
        <motion.div {...bubbleIn} className="flex justify-end">
          <UserBubble className="bg-red-600">
            <X className="w-3 h-3" /> Reject
          </UserBubble>
        </motion.div>
      );

    case 'enter-reject-reason':
      return (
        <motion.div {...bubbleIn} className="space-y-2.5">
          <SystemBubble>
            <AlertTriangle className="mr-1 inline h-3.5 w-3.5 -mt-0.5" />
            Record the clinical or administrative reason for rejection
          </SystemBubble>
          {isActive && <RejectReasonForm onContinue={props.onRejectReason} />}
        </motion.div>
      );

    case 'reject-reason-entered':
      return (
        <motion.div {...bubbleIn} className="flex justify-end">
          <UserBubble className="max-w-[85%] bg-red-600">{step.reason}</UserBubble>
        </motion.div>
      );

    case 'select-department':
      return (
        <motion.div {...bubbleIn} className="space-y-2.5">
          <SystemBubble>
            <Building2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Select department
          </SystemBubble>
          {isActive && (
            <div className="grid grid-cols-2 gap-2 ml-2">
              {props.departments.map((dept) => (
                <button
                  key={dept.id}
                  onClick={() => props.onDepartmentSelect(dept)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-xl border border-border bg-card',
                    'text-sm font-semibold text-foreground',
                    'hover:border-primary hover:bg-primary-50 hover:text-primary',
                    'active:scale-[0.97]',
                    'transition-all duration-150 cursor-pointer',
                  )}
                >
                  <span className="block">{departmentLabel(dept)}</span>
                  {dept.name_ar && (
                    <span className="block text-xs font-normal text-muted-foreground" dir="rtl">
                      {dept.name_ar}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      );

    case 'department-selected':
      return (
        <motion.div {...bubbleIn} className="flex justify-end">
          <UserBubble>
            <Building2 className="w-3 h-3" />
            {departmentLabel(step.department)}
          </UserBubble>
        </motion.div>
      );

    case 'select-doctor':
      return (
        <motion.div {...bubbleIn} className="space-y-2.5">
          <SystemBubble>
            <Stethoscope className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
            Select doctor
          </SystemBubble>
          {isActive && (
            <div className="grid grid-cols-1 gap-2 ml-2">
              {props.doctors.length === 0 && (
                <p className="text-xs text-muted-foreground px-1 py-2">
                  No doctors available for this department — add doctors in
                  Settings.
                </p>
              )}
              {props.doctors.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => props.onDoctorSelect(doc)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-xl border border-border bg-card',
                    'hover:border-primary hover:bg-primary-50',
                    'active:scale-[0.97]',
                    'transition-all duration-150 cursor-pointer',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-foreground">
                      {doc.name}
                    </p>
                    {doc.department_id == null && (
                      <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[0.625rem] text-muted-foreground">
                        Any department
                      </span>
                    )}
                  </div>
                  {doc.name_ar && (
                    <p className="text-xs text-muted-foreground mt-0.5" dir="rtl">
                      {doc.name_ar}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      );

    case 'doctor-selected':
      return (
        <motion.div {...bubbleIn} className="flex justify-end">
          <UserBubble>
            <Stethoscope className="w-3 h-3" />
            {step.doctor.name}
          </UserBubble>
        </motion.div>
      );

    case 'confirm-accept':
      return (
        <motion.div {...bubbleIn} className="space-y-2">
          <div className="chat-bubble-system px-3 py-2.5 max-w-[85%]">
            <p className="text-xs font-semibold text-foreground mb-1.5">
              Confirm your decision
            </p>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>
                <span className="font-medium text-foreground">Dept:</span>{' '}
                {departmentLabel(step.department)}
              </p>
              <p>
                <span className="font-medium text-foreground">Doctor:</span>{' '}
                {step.doctor.name}
              </p>
            </div>
          </div>
          {isActive && (
            <div className="flex gap-2 ml-2">
              <Button
                size="sm"
                className="rounded-lg gap-1.5 text-xs h-8"
                onClick={() =>
                  props.onConfirmAccept(step.doctor, step.department)
                }
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Confirm
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-lg gap-1.5 text-xs h-8"
                onClick={props.onGoBack}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Start Over
              </Button>
            </div>
          )}
        </motion.div>
      );

    case 'confirm-reject':
      return (
        <motion.div {...bubbleIn} className="space-y-2">
          <div className="chat-bubble-system px-3 py-2.5 max-w-[85%]">
            <div className="flex items-center gap-1.5 mb-1">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <p className="text-xs font-semibold text-foreground">
                Confirm rejection
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Reason: <span className="font-medium text-foreground">{step.reason}</span></p>
            <p className="mt-1 text-xs text-amber-600">This case will be marked as rejected.</p>
          </div>
          {isActive && (
            <div className="flex gap-2 ml-2">
              <Button
                size="sm"
                variant="destructive"
                className="rounded-lg gap-1.5 text-xs h-8"
                onClick={() => props.onConfirmReject(step.reason)}
              >
                <XCircle className="w-3.5 h-3.5" />
                Confirm Reject
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-lg gap-1.5 text-xs h-8"
                onClick={props.onGoBack}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Start Over
              </Button>
            </div>
          )}
        </motion.div>
      );

    case 'submitting':
      return (
        <motion.div {...bubbleIn} className="flex justify-center py-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-xs font-medium">Submitting...</span>
          </div>
        </motion.div>
      );

    case 'done':
      return (
        <motion.div
          {...bubbleIn}
          className="flex justify-center py-4"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 20,
                delay: 0.1,
              }}
              className={cn(
                'w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2',
                step.decision === 'accept'
                  ? 'bg-emerald-100 text-emerald-600'
                  : 'bg-red-100 text-red-600',
              )}
            >
              {step.decision === 'accept' ? (
                <CheckCircle2 className="w-6 h-6" />
              ) : (
                <XCircle className="w-6 h-6" />
              )}
            </motion.div>
            <p className="font-sans font-bold text-sm text-foreground">
              {step.decision === 'accept' ? 'Accepted' : 'Rejected'}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Redirecting...
            </p>
          </div>
        </motion.div>
      );

    default:
      return null;
  }
}

function RejectReasonForm({ onContinue }: { onContinue: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <div className="ml-2 space-y-2">
      <textarea
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why is this referral being rejected?"
        className="min-h-20 w-full rounded-xl border border-red-200 bg-card px-3 py-2 text-sm outline-none focus:border-red-400"
      />
      <Button size="sm" variant="destructive" disabled={!reason.trim()} onClick={() => onContinue(reason.trim())}>
        Continue
      </Button>
    </div>
  );
}

// ── Bubble primitives ─────────────────────────────────────────────────

function SystemBubble({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'chat-bubble-system px-3 py-2 max-w-[85%] text-xs font-medium',
        className,
      )}
    >
      {children}
    </div>
  );
}

function UserBubble({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'chat-bubble-user px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1',
        className,
      )}
    >
      {children}
    </div>
  );
}
